/* ==========================================================================
   SPLASH Weekly Draw — the data module, wired to the chain layer.

   This replaces the candidate build's hand-written rehearsal file. It publishes
   exactly the same five-method `window.SplashData` object, so weekly-view.js is
   untouched: every pixel of the design is the designer's, and every number in
   it now comes from the same reads the old raffle page uses.

   THE RULE THIS FILE IS BUILT TO: read the constants, never restate them. The
   three errors corrected on 2026-10-07 — a 24-block forfeit window that is
   really 26,182, an 1,800-block hour that is really 1,090, a 2-second block
   that is really 3.3 — were one mistake repeated: a number the realm already
   publishes, written down a second time by hand. So there are no chain
   constants in this file. Ticket price, entries per ticket and the week's
   length come from `economics()`, which reads the realm's own exported consts.
   The one exception is named where it occurs.

   WHAT "LIVE" MEANS HERE. The realm IS deployed on onyx now -- it went up on
   2026-10-07; game/ONYX-DEPLOY-2026-10-07.md has the heights and what was
   verified -- so this reads a real chain. `makeClient` and `makeDrawMock` share
   one interface precisely so this file does not have to care which, and the
   banner says which. It must never say "live" when it is not.

   This paragraph asserted "the realm is not deployed on onyx yet" for hours
   after that stopped being true, which is the exact shape of staleness the rule
   above is written against -- a statement that was correct when written and is
   now simply false.
   ========================================================================== */
import './polyfill.js';
import { CONFIG } from './config.js';
import * as live from './chain/reads.js';
import { makeClient, economics } from './chain/draws.js';
import { makeDrawMock } from './chain/drawmock.js';
import { readLive, readRecent, drawState, blockSecondsFor } from './draw.js';
import * as wallet from './chain/wallet.js';
import { realmAddress } from './chain/addr.js';

const REALM = CONFIG.raffleRealm;
const RECENT = 3;

// ugnot -> GNOT, and ONLY at the boundary. Every figure above this line is an
// exact BigInt of ugnot; the view wants plain numbers, so the lossy step
// happens once, here, where it can be seen.
const gnot = (u) => Number(u) / 1e6;

const S = {
  mode: 'rehearsal',
  client: null,
  econ: null,
  chain: null,
  liveStatus: null,
  deployed: false,
  live: null,
  recent: null,
  closeBlock: null,
  me: null,
  master: null,
  cadenceMs: 4000,
  err: {},
};

const listeners = [];
let state = null;

// The view's contract, copied from the candidate's own module rather than
// guessed: `{type:'state', state}` renders, `{type:'error', message}` shows the
// retry panel. Anything else is silently ignored by it, which is how an early
// version of this file managed to sit on "Reading the realm…" with no error.
function emit(evt) {
  for (const fn of listeners) {
    try { fn(evt); } catch (e) { console.error('SplashData listener threw', e); }
  }
}
const publish = () => emit({ type: 'state', state });
const fail = (message) => emit({ type: 'error', message });

/* --- the shape the view asks for, assembled from the reads ---------------- */
function compose() {
  const L = S.live;
  const econ = S.econ;

  // A failed read is a failed read. The design has an offline state; it is for
  // exactly this, and it is better than a stale number with no mark on it.
  if (!L || !econ) {
    state = { offline: true, error: (S.err.live ?? S.err.chain)?.message ?? 'the realm could not be read' };
    return state;
  }

  const phase = drawState(
    { committed: L.committed, resolved: false, abandoned: false, round: L.round },
    { index: L.index, current: L.index, beaconRound: null, liveTickets: L.entries },
  ) === 'selling' ? 'open' : L.committed ? 'drawing' : 'drawn';

  const rows = S.recent?.rows ?? [];
  const past = rows.filter((r) => r.index < L.index && r.resolved);

  state = {
    rehearsal: S.mode === 'rehearsal',
    network: CONFIG.chainId,
    // The REAL chain's height, even in rehearsal — it is what makes a rehearsal
    // visibly a rehearsal rather than a mock pretending to be a chain.
    networkHeight: S.liveStatus ? Number(S.liveStatus.height) : null,
    week: Number(L.index),
    phase,
    currentBlock: S.chain ? Number(S.chain.height) : null,
    closeBlock: S.closeBlock == null ? null : Number(S.closeBlock),
    // THE REHEARSAL'S BLOCKS REALLY DO ARRIVE AT ITS OWN CADENCE, so quoting
    // onyx's measured rate here would put the countdown out by the ratio
    // between them. blockSecondsFor returns {seconds, source, chainId}; taking
    // the object whole is how this page first printed "[object Object] seconds
    // a block".
    // CORRECTED 2026-10-07: this said `S.cadenceMs / 1000`, which is DOUBLE the
    // rehearsal's real rate -- drawmock advances two blocks per cadence tick
    // (drawmock.js: `st.height += 2n`), and exposes blockSeconds() saying so.
    // Restating a constant by hand, under a comment warning against exactly
    // that. Read the mock's own figure.
    secondsPerBlock: S.mode === 'rehearsal'
      ? S.client.blockSeconds()
      : blockSecondsFor(S.chain?.chainId ?? CONFIG.chainId).seconds,
    // The round pinned for THIS week, not the beacon's current head. The
    // distinction is the whole fairness claim: it was fixed before the week's
    // first entry existed.
    randomnessRound: L.round > 0n ? Number(L.round) : null,
    pot: { tickets: gnot(L.fromBuyers), flips: gnot(L.sponsored) },
    entries: { total: Number(L.entries), mine: S.me ? Number(L.mine) : null },
    ticket: {
      price: gnot(econ.ticketPrice),
      entries: Number(econ.entriesPerTicket),
      // The one constant not read from the realm: houseCutBps is unexported.
      // CONFIG carries it with a comment saying it must track the contracts,
      // and raffle_test.gno pins the contracts' own value.
      houseShare: Number(CONFIG.draw.houseCutBps) / 10000,
    },
    // The connected address, so the header can stop saying "Connect wallet"
    // after one is connected. null when nobody is.
    wallet: S.me ? { address: S.me } : null,
    // NOT WIRED YET, and null rather than invented. raffle.gno grew SponsorsOf
    // after this chain layer was written, so neither half can read it. The
    // design falls back to "SPLASH · WEEK n" on null, which is the honest
    // output until the read exists.
    sponsor: null,
    winner: null,
    // A DRAW THAT IS OVER AND NOT SETTLED. This is the one failure a player can
    // see and we cannot hide: the window closed, the money is still in the
    // contract, and nobody has pressed the buttons that finish it.
    //
    // IT IS DETECTED FROM THE CHAIN, NOT FROM OUR KEEPER. The page has no idea
    // whether our process is alive, out of gas, or was never started, and it
    // should not -- "the draw has not settled" is true and actionable in every
    // one of those cases, and it stays true if we disappear entirely. A banner
    // reading "our server is low on funds" would be both worse information and
    // the wrong abstraction.
    // Round and dueness, for the same reason as the hourly page -- see
    // hourly-data.js's note. "Anyone can finish it" is false while the
    // pinned drand round is still in the future, and the two states need
    // different sentences.
    stalled: (() => {
      const s = rows.find((r) => r.index < L.index && !r.resolved);
      if (!s) return null;
      const round = Number(s.round);
      const due = S.beaconRound == null || !round ? null : round <= S.beaconRound;
      return { index: Number(s.index), pot: gnot(s.pot), round, due };
    })(),
    history: past.map((r) => ({
      week: Number(r.index),
      winner: r.winner,
      amount: gnot(r.pot),
      fromFlips: gnot(r.sponsored),
      entries: Number(r.frozenTotal),
      round: Number(r.round),
    })),
    // PATHS, not URLs. The design prints these as text and links them itself;
    // handing it a full https:// URL made the proof panel render a wrapped
    // gnoweb address where a package path belongs.
    realm: REALM,
    beacon: CONFIG.beaconRealm,
    // A BASE URL, NOT A PACKAGE PATH. weekly-view.js:261 builds a link as
    // explorer + '/' + path-minus-gno.land, so handing it a realm path produced
    // href="gno.land/r/g1…/arcade/duel/r/g1…/arcade/raffle" -- two paths glued
    // together, pointing nowhere. Found by enumerating every link on every page
    // rather than by looking at one.
    explorer: CONFIG.gnoweb,
  };
  return state;
}

/* --- the poll ------------------------------------------------------------- */
async function refresh() {
  const c = S.client;
  const jobs = await Promise.allSettled([
    c.chainStatus(),
    readLive(c, S.me, 'raffle'),
    readRecent(c, RECENT, 'raffle'),
    c.currentEndHeight(),
    S.mode === 'live' ? live.currentRound() : Promise.resolve(null),
  ]);
  const [st, lv, rc, eh, br] = jobs;

  S.err = {};
  if (st.status === 'fulfilled') S.chain = st.value; else { S.chain = null; S.err.chain = st.reason; }
  if (lv.status === 'fulfilled') S.live = lv.value; else { S.live = null; S.err.live = lv.reason; }
  if (rc.status === 'fulfilled') S.recent = rc.value; else { S.recent = null; S.err.recent = rc.reason; }
  if (eh.status === 'fulfilled') S.closeBlock = BigInt(eh.value); else { S.closeBlock = null; S.err.close = eh.reason; }
  S.beaconRound = br.status === 'fulfilled' && br.value != null ? Number(br.value) : null;

  compose();
  if (state.offline) fail(state.error); else publish();
}

/* --- boot ----------------------------------------------------------------- */
async function start() {
  const q = new URLSearchParams(location.search);
  const forceDemo = q.has('demo') || location.hash === '#rehearsal';
  S.cadenceMs = Number(q.get('cadence') || 4000);

  try {
    S.liveStatus = await live.chainStatus();
    S.deployed = forceDemo ? false : await live.isDeployed(REALM);
  } catch (e) {
    console.warn('chain unreachable', e);
    S.err.chain = e;
  }

  if (S.deployed) {
    S.mode = 'live';
    S.client = makeClient('raffle');
    S.client.chainStatus = live.chainStatus;
    S.econ = await economics('raffle');
  } else {
    S.mode = 'rehearsal';
    S.client = makeDrawMock({ game: 'raffle', cadenceMs: S.cadenceMs });
    S.me = S.client.me;
    S.econ = S.client.economics();
  }


  // Carry a wallet across pages. Each of the three screens is a separate
  // document, so without this the arcade forgets you the moment you click
  // another game. Silent by design -- see wallet.resumeAdena.
  if (S.mode === 'live') {
    const back = (await wallet.resumeMnemonic().catch(() => null))
      ?? (await wallet.resumeAdena().catch(() => null));
    if (back) { S.me = back.address; S.master = back; }
  }
  await refresh();
  setInterval(refresh, S.mode === 'rehearsal' ? 1500 : CONFIG.pollMs);
}

/* --- the two write paths, deliberately not yet wired ---------------------- */
//
// Reads first, on purpose: the weekly screen is the one with the most live data
// and no money movement, so it proves the mapping without touching a payment
// path. Buying goes through the same allowance-then-call dance the old page
// already implements, and that is worth porting carefully rather than quickly.
// WIRED 2026-10-07, and this one DOES move money.
//
// THE FLOW IS COPIED FROM drawpage.js, NOT INVENTED. The old raffle page has
// implemented approve-then-buy against this realm for weeks; reproducing it from
// the realm's source would be a second implementation of a payment path, which
// is the last thing that should exist twice.
//
// TWO TRANSACTIONS, IN THIS ORDER, and the order is not a style choice:
//   1. wugnot.Approve(raffleAddress, cost) -- the realm PULLS through the GRC20
//      allowance; it never receives an attached send. (That is the same property
//      the contracts' own rejectAttachedCoin guard now enforces from the other
//      side, added this morning.)
//   2. raffle.Buy(count).
// A wallet prompt appears for each. If the player signs the first and refuses
// the second, they have granted an allowance and bought nothing -- harmless, and
// the next attempt skips step 1 because the allowance is already there.
//
// WRAPPING IS NOT DONE FOR THEM, deliberately. Tickets are priced in WRAPPED
// GNOT and a new wallet holds the native kind. Silently wrapping would be a
// third transaction the player never asked for, moving their money between two
// things they probably do not yet know are different. They get told, with the
// amount and the realm to do it at.
//
// I CANNOT TEST THE SIGNING PATH AND NEITHER CAN THE SUITE: it needs a real
// wallet extension, which a headless browser does not have. Every branch below
// that ends in a signature is unexercised code. The refusals and the arithmetic
// are testable and tested; the two `send` calls are not.
async function buyTickets(count) {
  const n = BigInt(Math.max(1, Math.floor(Number(count) || 1)));

  if (S.mode === 'rehearsal') {
    if (!S.client?.buy) throw new Error('The rehearsal cannot sell tickets.');
    await S.client.buy(n);
    await refresh();
    return { entries: Number(n) * Number(S.econ?.entriesPerTicket ?? 0) };
  }

  if (!S.master || !S.me) throw new Error('Connect a wallet first.');
  if (!S.econ) throw new Error('The realm has not been read yet — try again in a moment.');

  const cost = n * BigInt(S.econ.ticketPrice);
  const spender = realmAddress(REALM);

  // Work out what this purchase needs BEFORE asking for a signature, so the
  // player sees one dialog describing the whole thing rather than three.
  let wrap = 0n;
  let approve = null;
  const allowance = BigInt(await live.wugnotAllowance(S.me, spender));
  if (allowance < cost) {
    approve = cost;
    const held = BigInt(await live.wugnotBalance(S.me));
    if (held < cost) {
      wrap = cost - held;
      const native = BigInt(await live.ugnotBalance(S.me));
      const feeFloor = 2_000_000n;
      if (native < wrap + feeFloor) {
        throw new Error(
          'You need ' + gnot(wrap) + ' more GNOT wrapped and hold ' + gnot(native) + ' unwrapped. '
          + 'Top up from the faucet at https://faucet.gno.land first.',
        );
      }
    }
  }
  await sendBatch(msgsFor(REALM, 'Buy', [String(n)], { wrap, approve, spender }));
  await refresh();
  return { entries: Number(n) * Number(S.econ.entriesPerTicket) };
}



// Build the whole purchase as ONE transaction: wrap if needed, approve if
// needed, then buy. Returns the message list in order.
//
// ONE SIGNATURE INSTEAD OF THREE, which is what the owner asked for -- and it is
// not only fewer prompts. The messages are atomic: a failure anywhere reverts
// the lot, so there is no longer a state where the player wrapped and approved
// and then the purchase failed, leaving money converted for a thing they did not
// get. The three-prompt version could do exactly that.
function msgsFor(pkgPath, func, args, { wrap = 0n, approve = null, spender = null } = {}) {
  const m = S.master;
  if (!m) throw new Error('Connect a wallet first.');
  const json = m.kind === 'adena';
  const out = [];
  if (wrap > 0n) {
    out.push(json
      ? wallet.callJsonSend(S.me, CONFIG.wugnotRealm, 'Deposit', [], String(wrap) + 'ugnot')
      : wallet.callProtoSend(S.me, CONFIG.wugnotRealm, 'Deposit', [], String(wrap) + 'ugnot'));
  }
  if (approve != null) {
    out.push(json
      ? wallet.callJson(S.me, CONFIG.wugnotRealm, 'Approve', [spender, String(approve)])
      : wallet.callProto(S.me, CONFIG.wugnotRealm, 'Approve', [spender, String(approve)]));
  }
  out.push(json
    ? wallet.callJson(S.me, pkgPath, func, args)
    : wallet.callProto(S.me, pkgPath, func, args));
  return out;
}

function sendBatch(msgs) {
  const m = S.master;
  if (!m) throw new Error('Connect a wallet first.');
  if (m.kind === 'key') return wallet.keySend(m, msgs);
  if (m.kind === 'adena') return wallet.adenaSend(msgs);
  throw new Error('That wallet cannot sign from this page.');
}



// WIRED 2026-10-07, read-only. "wallet connect doesnt work" was the owner's
// second sentence after the site went public, and he was right: this rejected
// unconditionally while presenting a button that looked live. The machinery was
// already here in chain/wallet.js and had simply never been plumbed into the new
// screens.
//
// CONNECTING IS A READ, AND THAT IS THE WHOLE POINT OF DOING IT FIRST. All this
// obtains is an address, so the page can show YOUR entries instead of only the
// total. Nothing is signed, no allowance is granted, no money moves. The buy path
// above is still refused, deliberately -- an identity is cheap to get right and a
// payment path is not.
//
// ADENA'S NETWORK IS CHECKED, not assumed: connectAdena throws if the extension
// is pointed at a different chain. A wallet on the wrong network would otherwise
// return a perfectly valid address whose entries here are always zero, which
// looks like "the realm forgot me" rather than "you are on the wrong chain".
async function connectWallet() {
  if (S.mode === 'rehearsal') {
    S.me = S.client?.me ?? null;
    await refresh();
    return { address: S.me, kind: 'rehearsal' };
  }

  wallet.clearDisconnected(); // an explicit connect overrides an earlier disconnect
  const resumed = await wallet.resumeMnemonic().catch(() => null);
  const w = resumed ?? (wallet.hasAdena()
    ? await wallet.connectAdena()
    : (() => { throw new Error('No wallet found. Install Adena and point it at ' + CONFIG.chainId + '.'); })());

  S.me = w.address;
  S.master = w;
  await refresh();
  // The view toasts res.message on success. Returning only {address, kind}
  // made it toast the word "undefined" -- found by reading the view's handler
  // rather than by seeing it, because the happy path needs a real extension.
  return {
    address: w.address,
    kind: w.kind,
    message: 'Connected ' + w.address.slice(0, 8) + '…' + w.address.slice(-4) + '. Your entries will show here.',
  };
}


// Disconnect. Forgets the wallet HERE and nothing else: a page cannot un-approve
// itself in Adena, and pretending otherwise would be a lie about what happened.
// The flag is what makes it stick across the other two screens.
async function disconnect() {
  wallet.forgetMnemonic();
  wallet.markDisconnected();
  S.me = null;
  S.master = null;
  await refresh();
  return { message: 'Disconnected here. Adena still has this site approved \u2014 remove it in the extension to undo that.' };
}

window.SplashData = {
  config: { mode: S.mode, network: CONFIG.chainId, realm: REALM },
  start,
  onChange(fn) { listeners.push(fn); if (state && !state.offline) fn({ type: 'state', state }); },
  buyTickets,
  connectWallet,
  disconnect,
};
